import * as d3 from "d3";

// The viewer a GitHub diagram has, on d3's zoom: docs/specs/2026-10-18-readme-markdown.md#7--diagrams-get-a-viewer
export const PAN_STEP = 50;
export const ZOOM_STEP = 1.25;
export const SCALE_EXTENT = [0.25, 8];

/** The SVG's viewBox as d3-zoom's extent, read off the attribute so it holds where nothing is laid out. */
function viewBoxExtent(svg) {
  const box = (svg.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number);
  if ( 4 === box.length && box.every(Number.isFinite) && box[2] > 0 && box[3] > 0 ) {
    return [[box[0], box[1]], [box[0] + box[2], box[1] + box[3]]];
  }
  return [[0, 0], [1, 1]];
}

/** CSS pixels per viewBox unit as the SVG is drawn; 1 where it is not laid out. */
function pixelsPerUnit(svg, extent) {
  const rect = svg.getBoundingClientRect();
  const scale = Math.min(rect.width / (extent[1][0] - extent[0][0]), rect.height / (extent[1][1] - extent[0][1]));
  return scale > 0 && Number.isFinite(scale) ? scale : 1;
}

/**
 * Lets d3's zoom move a drawn mermaid diagram: the SVG's content goes into one group, which every gesture transforms.
 * Dragging pans; the wheel and touch move it only while `host` is the full-screen element, so the page scrolls past.
 *
 * @param host the diagram's `.playground-mermaid`, whose `.playground-mermaid-canvas` holds the drawn SVG
 * @return { pan(dx, dy), zoomIn(), zoomOut(), reset() }, or null when there is no SVG
 */
export function attachViewer(host) {
  const svg = host.querySelector(".playground-mermaid-canvas > svg");
  if ( !svg ) {
    return null;
  }
  const viewport = document.createElementNS("http://www.w3.org/2000/svg", "g");
  viewport.setAttribute("class", "playground-mermaid-viewport");
  while ( svg.firstChild ) {
    viewport.appendChild(svg.firstChild);
  }
  svg.appendChild(viewport);

  const extent = viewBoxExtent(svg);
  const fullScreen = () => document.fullscreenElement === host;
  const zoom = d3.zoom()
    .extent(() => extent)
    .scaleExtent(SCALE_EXTENT)
    .filter(() => {
      const event = d3.event;
      if ( "wheel" === event.type || event.type.startsWith("touch") ) {
        return fullScreen();
      }
      return !event.ctrlKey && !event.button;
    })
    .on("zoom", () => viewport.setAttribute("transform", d3.event.transform));
  const selection = d3.select(svg).call(zoom).on("dblclick.zoom", null);
  // d3 turns touch scrolling off on the SVG; markdown.css turns it off again only in full screen.
  svg.style.removeProperty("touch-action");

  return {
    /** Moves the view one step towards a side: (1, 0) shows what lies to the right. */
    pan(dx, dy) {
      const step = PAN_STEP / pixelsPerUnit(svg, extent);
      const now = d3.zoomTransform(svg);
      selection.call(zoom.transform, d3.zoomIdentity.translate(now.x - dx * step, now.y - dy * step).scale(now.k));
    },
    zoomIn() {
      selection.call(zoom.scaleBy, ZOOM_STEP);
    },
    zoomOut() {
      selection.call(zoom.scaleBy, 1 / ZOOM_STEP);
    },
    /** Back to the drawing as mermaid fitted it. */
    reset() {
      selection.call(zoom.transform, d3.zoomIdentity);
    },
  };
}
